import { createContext, useContext, useCallback } from "react";
import { useToast } from "../hooks/useToast";
import { handleError as handleErrorUtil, getUserFriendlyMessage } from "../utils/errorHandler";
import ToastContainer from "../components/ToastContainer";

/**
 * Error Context
 * Provides global error handling capabilities throughout the app
 * 
 * Requirements: 19.4, 36.2
 */
const ErrorContext = createContext(null);

export function ErrorProvider({ children }) {
  const { error: showErrorToast } = useToast();

  /**
   * Handle error with logging and toast notification
   * @param {Error} error - The error object
   * @param {string} context - Context where the error occurred
   * @param {object} options - Additional options
   */
  const handleError = useCallback(
    (error, context = "Unknown", options = {}) => {
      handleErrorUtil(error, context, showErrorToast, options);
    },
    [showErrorToast]
  );

  /**
   * Show error message directly
   * @param {string} message - Error message to display
   * @param {string} context - Context for logging
   */
  const showError = useCallback(
    (message, context = "User Error") => {
      console.error(`[${context}] ${message}`);
      showErrorToast(message);
    },
    [showErrorToast]
  );

  /**
   * Handle operation error with custom message
   * @param {Error} error - The error object
   * @param {string} operation - Operation name (e.g., "Create Invoice")
   */
  const handleOperationError = useCallback(
    (error, operation) => {
      const message = `Failed to ${operation.toLowerCase()}. ${getUserFriendlyMessage(error)}`;
      handleError(error, operation, { userMessage: message });
    },
    [handleError]
  );

  const value = {
    handleError,
    showError,
    handleOperationError,
  };

  return (
    <ErrorContext.Provider value={value}>
      {children}
      <ToastContainer />
    </ErrorContext.Provider>
  );
}

/**
 * Hook to use error handling
 * @returns {object} Error handling functions
 */
export function useError() {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error("useError must be used within ErrorProvider");
  }
  return context;
}

export default ErrorContext;
